import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { COLUMNS } from "./columns";
import {
  useTable,
  useRowSelect,
  useExpanded,
  usePagination
} from "react-table";
import { noDataPresent, tooltiptextId } from "../../../../../../locales/en";
import TableSkeleton from "../../../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../../../hooks/useVisibleColumnCount";
import { Link } from "react-router-dom";
import ExpandToggle from "../../../../../common/ExpandToggle";
import { Tooltip } from "react-tooltip";
import DualPagination from "../../../../../common/DualPagination";
import { INVOICE_DETAILS } from "../../../../../../route/paths";
import {
  cleanQuoteId,
  formatDateWithDayjs,
  showToast
} from "../../../../../../utils/utils";
import {
  PAGE_INDEX,
  PAGE_SIZE,
  showToastType
} from "../../../../../../utils/constants/appConstants";
import { getOverdueRptQuoteDetailsUrl } from "../../../../../../utils/constants/apiEndPoints";
import privateAxios from "../../../../../../utils/axiosPrivate";
import { DATE_COLUMNS } from "../../../../../../utils/constants/dateFields";

const SalesInvoiceReportModalTable = (props) => {
  const { invoiceIdsListRef } = props;
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const initialCustomerModalData = null;
  const [customerModalData, setCustomerModalData] = useState(
    initialCustomerModalData
  );
  const [loading, setLoading] = useState(false);
  const columns = useMemo(() => COLUMNS, []);

  const invoiceDataIds = invoiceIdsListRef.current;

  const tableData = useMemo(
    () =>
      Array.isArray(customerModalData?.invoices)
        ? customerModalData.invoices
        : [],
    [customerModalData?.invoices]
  );

  const [expandedRows, setExpandedRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data: tableData,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE }
    },

    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [...columns]);
    }
  );

  const { pageIndex } = state;

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };
  useEffect(() => {
    const handleInvoiceModalData = async () => {
      try {
        setLoading(true);
        const formattedIds = invoiceDataIds
          ? invoiceDataIds.split(",").map((id) => parseInt(id.trim(), 10))
          : [];

        const response = await privateAxios.post(
          getOverdueRptQuoteDetailsUrl,
          formattedIds,
          {
            params: {
              page: pageIndex,
              size: pageSize
            }
          }
        );
        if (response) {
          setCustomerModalData(response?.data?.data || response);
          setTotalPages(response?.data?.pageInfo?.totalPages || 0);
        }
      } catch (error) {
        showToast(error.response?.data?.message, showToastType.error);
      } finally {
        setLoading(false);
      }
    };
    handleInvoiceModalData();
  }, [invoiceDataIds, pageIndex, pageSize]);

  return (
    <div className="col-md-12">
      <table
        className={`mt-2 mb-3 reportable1 inv-monthly-table bgless_table`}
        {...getTableProps()}
      >
        <thead ref={headerRef}>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} className="parentrow" {...rest}>
                <th />
                {headerGroup.headers.map((column) => (
                  <th key={column.id} {...column.getHeaderProps()}>
                    <span>{column.render("Header")}</span>
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading && <TableSkeleton rows={10} columns={visibleColumnCount} />}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                {noDataPresent}
              </td>
            </tr>
          )}
          {!loading &&
            rows.length > 0 &&
            rows.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              return (
                <React.Fragment key={row.id}>
                  <tr className="parentrow" {...row.getRowProps()}>
                    <td className="expand-td">
                      <ExpandToggle
                        isExpanded={isExpanded}
                        onToggle={() => toggleRow(row.id)}
                        id={tooltiptextId.monthlyKpiReportId}
                      />
                    </td>

                    {row.cells.map((cell) => {
                      let content = cell.render("Cell");
                      if (cell.column.id === "FTQuoteID") {
                        const cleanId = cleanQuoteId(
                          cell.row.original.FTQuoteID
                        );
                        content = (
                          <Link to={INVOICE_DETAILS(cleanId)}>
                            {cell.render("Cell")}
                          </Link>
                        );
                      }

                      if (DATE_COLUMNS.includes(cell.column.id)) {
                        content = cell.value
                          ? formatDateWithDayjs(cell.value, "DD-MMM-YYYY")
                          : "";
                      }
                      return (
                        <td
                          key={cell.getCellProps().key}
                          {...cell.getCellProps()}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={columns.length + 2}>
                        <div className="tdbody">
                          {columns.map((column) => {
                            let value = row.original[column.accessor];
                            const accessor = column.accessor;
                            if (
                              accessor === "dateCompleted" &&
                              (value === null || value === undefined)
                            ) {
                              value = "";
                            }

                            if (DATE_COLUMNS.includes(accessor)) {
                              value = value
                                ? formatDateWithDayjs(value, "DD-MMM-YYYY")
                                : "";
                            }
                            return (
                              <span
                                key={column.accessor}
                                data-label={column.Header}
                              >
                                {String(value ?? "")}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
      {tableData && tableData.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={tableData.length}
        />
      )}
      <Tooltip id={tooltiptextId.monthlyKpiReportId} place="top" />
    </div>
  );
};

SalesInvoiceReportModalTable.propTypes = {
  invoiceIdsListRef: PropTypes.object
};
export default SalesInvoiceReportModalTable;
